import './EmlToken.css';

import { useState, useEffect } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import {
    GET_TOKEN_BY_ID,
    REDEEM_EML_TOKEN,
    GET_PROPERTIES,
    useLazyQuery,
    useQuery,
    useMutation,
} from 'utils/graphql';
import { formatUTCDateTime } from 'utils/date.util';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import User from 'utils/user.util';
import { useTranslation } from 'react-i18next';

const EmlToken = () => {
    const { t } = useTranslation();
    const [emlTokenQuery, setEmlTokenQuery] = useState('');
    const [emlToken, setEmlToken] = useState(null);
    const [showRedemptionModal, setShowRedemptionModal] = useState(false);

    const [idVerified, setIdVerified] = useState(false);
    const [formSigned, setFormSigned] = useState(false);
    const [redeemCenter, setRedeemCenter] = useState('');
    const [properties, setProperties] = useState([]);
    const [comments, setComments] = useState('');

    const [formErrors, setFormErrors] = useState({
        idVerified: '',
        formSigned: '',
        redemption: '',
    });
    const [validated, setValidated] = useState(false);
    const [isRedeeming, setIsRedeeming] = useState(false);

    const tokenFields = [
        {
            key: 'last_4_digits',
            label: 'last_4_digits',
        },
        {
            key: 'program_name',
            label: 'program_name',
        },
        {
            key: 'merchant_group_name',
            label: 'merchant_group_name',
        },
        {
            key: 'amount',
            label: 'amount',
        },
        {
            key: 'status',
            label: 'status',
        },
        {
            key: 'card_id',
            label: 'card_id',
        },
        {
            key: 'recipient_email',
            label: 'recipient_email',
        },
        {
            key: 'recipient_name',
            label: 'recipient_name',
        },
        {
            key: 'customer_email',
            label: 'customer_email',
        },
        {
            key: 'customer_name',
            label: 'customer_name',
        },
        {
            key: 'order_id',
            label: 'order_id',
        },
        {
            key: 'redeem',
            label: 'redeemed',
        },
        {
            key: 'updated_at',
            label: 'redeem_date',
        },
        {
            key: 'comment',
            label: 'comment',
        },
    ];

    const { loading: loadingProperties } = useQuery(GET_PROPERTIES, {
        onCompleted: (data) => {
            setProperties(data.table.records.map((record) => record.property__c));
        },
    });

    const [getTokenById, { loading }] = useLazyQuery(GET_TOKEN_BY_ID, {
        fetchPolicy: 'cache-and-network',
    });

    const [redeemEmlToken] = useMutation(REDEEM_EML_TOKEN);

    const searchEmlToken = async () => {
        setEmlToken(null);
        const { data, error } = await getTokenById({
            variables: {
                token_id: emlTokenQuery,
            },
        });

        if (data && data.table.count > 0) {
            setEmlToken(data.table.records[0].eml_token);
        }
    };

    const resetModal = () => {
        setIdVerified(false);
        setFormSigned(false);
        setRedeemCenter('');
        setComments('');
        setFormErrors({
            idVerified: '',
            formSigned: '',
            redemption: '',
        });
        setValidated(false);
        setIsRedeeming(false);
    };

    const validate = () => {
        let _formErrors = {
            idVerified: '',
            formSigned: '',
            redemption: '',
        };
        setFormErrors(_formErrors);
        let errors = false;
        if (!idVerified) {
            _formErrors.idVerified = 'id_must_be_verified';
            errors = true;
        }
        if (!formSigned) {
            _formErrors.formSigned = 'card_status_change_form_error';
            errors = true;
        }

        if (errors) {
            setFormErrors(_formErrors);
            setValidated(true);
            return false;
        }
        return true;
    };

    const redeem = () => {
        setIsRedeeming(true);
        if (validate()) {
            redeemEmlToken({
                variables: {
                    id: emlToken.id,
                    redeem_center: redeemCenter,
                    comment: comments,
                    user_id: User.getUser()?.sfid,
                    date: formatUTCDateTime(),
                },
            })
                .then((response) => {
                    if (response.errors) {
                        setFormErrors({
                            ...formErrors,
                            redemption: 'eml_token_redeem_fail',
                        });
                        setIsRedeeming(false);
                    } else {
                        setShowRedemptionModal(false);
                        const updatedEmlToken = response.data.updateRow.eml_token;
                        setEmlToken({
                            ...emlToken,
                            updated_at: updatedEmlToken.updated_at,
                            redeem: updatedEmlToken.redeem,
                            comment: updatedEmlToken.comment,
                            user_id: updatedEmlToken.user_id,
                            redeem_centre: updatedEmlToken.redeem_centre,
                        });
                    }
                })
                .catch((error) => {
                    console.log('error', error);
                    setFormErrors({
                        ...formErrors,
                        redemption: 'eml_token_redeem_fail',
                    });
                    setIsRedeeming(false);
                });
        } else {
            setIsRedeeming(false);
        }
    };

    const formatTokenProperty = (key, value) => {
        switch (key) {
            case 'amount':
                return '$' + parseFloat(value).toFixed(2);
            case 'updated_at':
                if (value) return formatUTCDateTime(new Date(value), false, '/');
                return '';
            case 'redeem':
                return t(value.toString());
            default:
                return value;
        }
    };

    useEffect(() => {
        if (!emlTokenQuery || emlTokenQuery.length === 0) return;
        const timer = setTimeout(() => searchEmlToken(), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [emlTokenQuery]);

    useEffect(() => {
        if (validated) setValidated(false);
    }, [idVerified, formSigned]);

    useEffect(() => {
        if (!showRedemptionModal) return;
        resetModal();
    }, [showRedemptionModal]);

    return (
        <div className="eml-token">
            <Form>
                <Form.Group>
                    <Form.Label>{t('eml_token')}</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder={t('search')}
                        value={emlTokenQuery}
                        onChange={(event) => setEmlTokenQuery(event.target.value)}
                    />
                </Form.Group>
            </Form>
            {loading && (
                <div className="d-flex justify-content-center mt-4">
                    <Spinner></Spinner>
                </div>
            )}
            {emlToken && (
                <>
                    <Table bordered className="mt-4 eml-table">
                        <tbody>
                            {tokenFields.map((tokenField, i) => {
                                return (
                                    <tr
                                        key={i}
                                        className={`${
                                            tokenField.key === 'redeem' && 'redeem-row'
                                        } ${
                                            tokenField.key === 'redeem' &&
                                            emlToken[tokenField.key] &&
                                            'redeemed'
                                        } ${
                                            tokenField.key === 'status' &&
                                            emlToken[tokenField.key].toLowerCase() == 'locked' &&
                                            'locked'
                                        } `}
                                    >
                                        <td className="label-col">{t(tokenField.label)}:</td>
                                        <td>
                                            {formatTokenProperty(
                                                tokenField.key,
                                                emlToken[tokenField.key]
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    {!emlToken.redeem && emlToken.status.toLowerCase() !== 'locked' && (
                        <Button
                            variant="success"
                            className="mt-2"
                            onClick={() => setShowRedemptionModal(true)}
                        >
                            {t('redeem')}
                        </Button>
                    )}
                </>
            )}
            {emlToken && (
                <Modal show={showRedemptionModal} onHide={() => setShowRedemptionModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <h5>
                                {t('redeem_token')}: {emlToken.external_id}
                            </h5>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {loadingProperties ? (
                            <div className="d-flex justify-content-center align-items-center">
                                <Spinner></Spinner>
                            </div>
                        ) : (
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Check>
                                        <Form.Check.Input
                                            required
                                            isInvalid={validated && formErrors.idVerified}
                                            checked={idVerified}
                                            onChange={(event) =>
                                                setIdVerified(event.target.checked)
                                            }
                                        />
                                        <Form.Check.Label>{t('id_verified')}</Form.Check.Label>
                                        <Form.Control.Feedback type="invalid" className="fw-bold">
                                            {t(formErrors.idVerified)}
                                        </Form.Control.Feedback>
                                    </Form.Check>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Check>
                                        <Form.Check.Input
                                            required
                                            isInvalid={validated && formErrors.formSigned}
                                            checked={formSigned}
                                            onChange={(event) =>
                                                setFormSigned(event.target.checked)
                                            }
                                        />
                                        <Form.Check.Label>
                                            {t('card_status_change_form')}
                                        </Form.Check.Label>
                                        <Form.Control.Feedback type="invalid" className="fw-bold">
                                            {t(formErrors.formSigned)}
                                        </Form.Control.Feedback>
                                    </Form.Check>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('redeem_center')}</Form.Label>
                                    <Form.Select
                                        value={redeemCenter}
                                        onChange={(event) => setRedeemCenter(event.target.value)}
                                    >
                                        {properties.map((property, i) => (
                                            <option value={property.name} key={i}>
                                                {property.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>{t('comment')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                    />
                                </Form.Group>
                                {formErrors.redemption && (
                                    <div
                                        className="invalid-feedback mt-3 fw-bold"
                                        style={{
                                            display: formErrors.redemption ? 'block' : 'none',
                                        }}
                                    >
                                        {t(formErrors.redemption)}
                                    </div>
                                )}
                            </Form>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <LoadingButton onClick={redeem} isLoading={isRedeeming}>
                            {t('redeem')}
                        </LoadingButton>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default EmlToken;
